import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Pagination,
  Collapse,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Refresh as RefreshIcon, 
  ExpandMore as ExpandMoreIcon, 
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { userManagementService, CustomerV2 } from '../../services/userManagementService';
import type { UserAccessLevel } from '../../services/authService';

interface UserAssignment {
  id: string;
  client_id: string;
  red_rock_id: string;
  red_rock_full_entity_name?: string; // This field exists in the assignment record
  is_active: boolean;
  is_current_version: boolean;
  user_access_type?: 'can_view' | 'can_edit';
  assigned_date: string;
  created?: string;
  created_at?: string;
  updated?: string;
  updated_at?: string;
  expand?: {
    client_id?: {
      id: string;
      email: string;
      username: string;
      verified: boolean;
    };
    red_rock_id?: {
      id: string;
      red_rock_full_entity_name?: string;
      company_name?: string;
    };
  };
}

interface GroupedAssignment {
  userId: string;
  userEmail: string;
  username: string;
  verified: boolean;
  assignments: UserAssignment[];
  customerCount: number;
}

interface UserAssignmentsProps {
  userAccessLevel?: UserAccessLevel;
}

const UserAssignments: React.FC<UserAssignmentsProps> = ({ userAccessLevel = 'normal' }) => {
  const [assignments, setAssignments] = useState<UserAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const isAllAccess = userAccessLevel === 'all_access' || userAccessLevel === 'admin';
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const perPage = 50;

  // User option for assignment (client to assign to)
  type UserOption = { id: string; email: string; username?: string };

  // Add Assignment Dialog State
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerV2 | null>(null);
  const [availableCustomers, setAvailableCustomers] = useState<CustomerV2[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<UserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userAccessType, setUserAccessType] = useState<'can_view' | 'can_edit'>('can_view');
  const [creatingAssignment, setCreatingAssignment] = useState(false);
  const [addDialogUserLocked, setAddDialogUserLocked] = useState(false);

  // Edit Assignment Dialog State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<UserAssignment | null>(null);
  const [updatingAssignment, setUpdatingAssignment] = useState(false);
  const [deletingAssignment, setDeletingAssignment] = useState<string | null>(null);

  const loadAssignments = useCallback(async (currentPage: number = 1, search: string = '') => {
    try {
      setLoading(true);
      setError(null);
      const result = await userManagementService.fetchAllUserAssignments(currentPage, perPage, search);
      setAssignments(result.items);
      setTotalPages(result.totalPages);
      setTotalItems(result.totalItems);
    } catch (err: any) {
      console.error('Error loading assignments:', err);
      setError(err.message || 'Failed to load user assignments');
    } finally {
      setLoading(false);
    }
  }, [perPage]);

  useEffect(() => {
    loadAssignments(page, searchTerm);
  }, [page, searchTerm, loadAssignments]);

  const handleSearch = () => {
    setPage(1);
    loadAssignments(1, searchTerm);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleRefresh = () => {
    loadAssignments(page, searchTerm);
  };

  const toggleUserExpand = (userId: string) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  // Load users for add assignment dialog with debouncing (search-as-you-type)
  const userSearchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const loadUsers = useCallback(async (search: string = '') => {
    if (userSearchTimerRef.current) {
      clearTimeout(userSearchTimerRef.current);
    }
    userSearchTimerRef.current = setTimeout(async () => {
      try {
        setLoadingUsers(true);
        const result = await userManagementService.fetchExistingUsers(1, 50, search.trim() || undefined);
        setAvailableUsers(result.items.map((u: any) => ({ id: u.id, email: u.email || '', username: u.username })));
      } catch (err: any) {
        console.error('Error loading users:', err);
        setError(err.message || 'Failed to load users');
      } finally {
        setLoadingUsers(false);
      }
    }, 300);
  }, []);

  // Load customers for add assignment dialog with debouncing
  const customerSearchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const loadCustomers = useCallback(async (search: string = '') => {
    // Clear existing timer
    if (customerSearchTimerRef.current) {
      clearTimeout(customerSearchTimerRef.current);
    }

    // Debounce the search
    customerSearchTimerRef.current = setTimeout(async () => {
      try {
        setLoadingCustomers(true);
        const customers = await userManagementService.fetchCustomersForAssignment(search);
        setAvailableCustomers(customers);
      } catch (err: any) {
        console.error('Error loading customers:', err);
        setError(err.message || 'Failed to load customers');
      } finally {
        setLoadingCustomers(false);
      }
    }, 300); // 300ms debounce
  }, []);

  // Open add assignment dialog (optionally with a user pre-selected, e.g. from row "Add Assignment")
  const handleOpenAddDialog = (preSelectedUser?: UserOption) => {
    setAddDialogOpen(true);
    setSelectedUser(preSelectedUser || null);
    setSelectedCustomer(null);
    setUserAccessType('can_view');
    setAddDialogUserLocked(!!preSelectedUser);
    if (preSelectedUser) {
      setAvailableUsers([preSelectedUser]);
    } else {
      setAvailableUsers([]);
    }
    loadCustomers(''); // Load initial customers
  };

  // Close add assignment dialog
  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setSelectedUser(null);
    setSelectedCustomer(null);
    setUserAccessType('can_view');
    setAddDialogUserLocked(false);
    if (customerSearchTimerRef.current) {
      clearTimeout(customerSearchTimerRef.current);
    }
    if (userSearchTimerRef.current) {
      clearTimeout(userSearchTimerRef.current);
    }
  };

  // Create new assignment
  const handleCreateAssignment = async () => {
    if (!selectedUser || !selectedCustomer) {
      setError('Please select both a user and a customer');
      return;
    }

    try {
      setCreatingAssignment(true);
      setError(null);
      
      const assignmentData = {
        client_id: selectedUser.id,
        red_rock_id: selectedCustomer.id,
        red_rock_full_entity_name: selectedCustomer.red_rock_full_entity_name || selectedCustomer.company_name || '',
        is_active: true,
        user_access_type: userAccessType,
        assigned_date: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
        is_current_version: true,
      };

      await userManagementService.createUserAssignments([assignmentData]);
      
      setSuccess('Assignment created successfully');
      handleCloseAddDialog();
      loadAssignments(page, searchTerm); // Refresh the list
    } catch (err: any) {
      console.error('Error creating assignment:', err);
      setError(err.message || 'Failed to create assignment');
    } finally {
      setCreatingAssignment(false);
    }
  };

  // Open edit assignment dialog
  const handleOpenEditDialog = (assignment: UserAssignment) => {
    setEditingAssignment(assignment);
    setEditDialogOpen(true);
  };

  // Close edit assignment dialog
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingAssignment(null);
  };

  // Update assignment
  const handleUpdateAssignment = async () => {
    if (!editingAssignment) return;

    try {
      setUpdatingAssignment(true);
      setError(null);

      // Ensure is_active is a proper boolean (true or false, never undefined)
      // Since is_active is required in the schema, we must always provide it
      let isActiveValue: boolean;
      if (editingAssignment.is_active === true || editingAssignment.is_active === false) {
        isActiveValue = editingAssignment.is_active;
      } else {
        // If somehow undefined, default to true
        isActiveValue = true;
      }

      const updateData: any = {
        is_active: isActiveValue,
      };

      console.log('Updating assignment with data:', updateData);
      console.log('is_active type:', typeof isActiveValue, 'value:', isActiveValue);
      console.log('updateData.is_active:', updateData.is_active, 'type:', typeof updateData.is_active);

      await userManagementService.updateAssignment(editingAssignment.id, updateData);
      
      setSuccess('Assignment updated successfully');
      handleCloseEditDialog();
      loadAssignments(page, searchTerm); // Refresh the list
    } catch (err: any) {
      console.error('Error updating assignment:', err);
      setError(err.message || 'Failed to update assignment');
    } finally {
      setUpdatingAssignment(false);
    }
  };

  // Delete (deactivate) assignment
  const handleDeleteAssignment = async (assignmentId: string) => {
    if (!window.confirm('Are you sure you want to remove this assignment? This will deactivate it.')) {
      return;
    }

    try {
      setDeletingAssignment(assignmentId);
      setError(null);

      await userManagementService.deleteAssignment(assignmentId);
      
      setSuccess('Assignment removed successfully');
      loadAssignments(page, searchTerm); // Refresh the list
    } catch (err: any) {
      console.error('Error deleting assignment:', err);
      setError(err.message || 'Failed to delete assignment');
    } finally {
      setDeletingAssignment(null);
    }
  };

  // Group assignments by user
  const groupedAssignments = useMemo(() => {
    const grouped: Map<string, GroupedAssignment> = new Map();

    assignments.forEach((assignment) => {
      const userId = assignment.client_id;
      const user = assignment.expand?.client_id;

      if (!user) {
        // Skip if user data is not available
        return;
      }

      if (!grouped.has(userId)) {
        grouped.set(userId, {
          userId,
          userEmail: user.email || 'Unknown User',
          username: user.username || 'N/A',
          verified: user.verified || false,
          assignments: [],
          customerCount: 0,
        });
      }

      const group = grouped.get(userId)!;
      group.assignments.push(assignment);
      group.customerCount = group.assignments.length;
    });

    return Array.from(grouped.values()).sort((a, b) => {
      const emailA = a.userEmail || '';
      const emailB = b.userEmail || '';
      return emailA.localeCompare(emailB);
    });
  }, [assignments]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const getCustomerName = (assignment: UserAssignment): string => {
    // Try to get customer name from expanded relation
    const customer = assignment.expand?.red_rock_id;
    if (customer) {
      return customer.red_rock_full_entity_name || customer.company_name || 'Unknown Customer';
    }
    // Fallback: use red_rock_full_entity_name if available in the assignment record itself
    if (assignment.red_rock_full_entity_name) {
      return assignment.red_rock_full_entity_name;
    }
    // Last resort: show the red_rock_id
    return `Customer ID: ${assignment.red_rock_id}`;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          User Assignments {isAllAccess ? '' : '(Read-Only)'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {isAllAccess && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenAddDialog()}
            sx={{ mr: 1 }}
          >
            Add Assignment
          </Button>
          )}
          <Tooltip title="Refresh list">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          fullWidth
          placeholder="Search by user email, username, or customer name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={handleSearchKeyPress}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          size="small"
        />
        <Button variant="contained" onClick={handleSearch} disabled={loading}>
          Search
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="40px"></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Username</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong># Customers</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedAssignments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {searchTerm ? 'No assignments found matching your search.' : 'No user assignments found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  groupedAssignments.map((group) => (
                    <React.Fragment key={group.userId}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleUserExpand(group.userId)}
                          >
                            {expandedUsers.has(group.userId) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{group.userEmail}</TableCell>
                        <TableCell>{group.username}</TableCell>
                        <TableCell>
                          <Chip
                            label={group.verified ? 'Verified' : 'Unverified'}
                            color={group.verified ? 'success' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={group.customerCount}
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => toggleUserExpand(group.userId)}
                          >
                            {expandedUsers.has(group.userId) ? 'Hide' : 'View'} Details
                          </Button>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0, border: 0, px: 0 }}>
                          <Collapse in={expandedUsers.has(group.userId)} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Customer Assignments:
                                </Typography>
                                {isAllAccess && (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleOpenAddDialog({ id: group.userId, email: group.userEmail, username: group.username })}
                                    sx={{ ml: 2 }}
                                  >
                                    Add Assignment
                                  </Button>
                                )}
                              </Box>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell><strong>Customer Name</strong></TableCell>
                                    <TableCell><strong>Assignment Date</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    {isAllAccess && <TableCell align="right"><strong>Actions</strong></TableCell>}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {group.assignments.map((assignment) => (
                                    <TableRow key={assignment.id}>
                                      <TableCell>{getCustomerName(assignment)}</TableCell>
                                      <TableCell>{formatDate(assignment.assigned_date)}</TableCell>
                                      <TableCell>
                                        <Chip
                                          label={assignment.is_active ? 'Active' : 'Inactive'}
                                          color={assignment.is_active ? 'success' : 'default'}
                                          size="small"
                                        />
                                      </TableCell>
                                      {isAllAccess && (
                                        <TableCell align="right">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleOpenEditDialog(assignment)}
                                            color="primary"
                                            sx={{ mr: 1 }}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleDeleteAssignment(assignment.id)}
                                            color="error"
                                            disabled={deletingAssignment === assignment.id}
                                          >
                                            {deletingAssignment === assignment.id ? (
                                              <CircularProgress size={16} />
                                            ) : (
                                              <DeleteIcon fontSize="small" />
                                            )}
                                          </IconButton>
                                        </TableCell>
                                      )}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            Showing {groupedAssignments.length} user{groupedAssignments.length !== 1 ? 's' : ''} with {totalItems} total assignment{totalItems !== 1 ? 's' : ''}
          </Typography>
        </>
      )}

      {/* Add Assignment Dialog */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Assignment</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {addDialogUserLocked && selectedUser ? (
              <TextField
                fullWidth
                label="User"
                value={selectedUser.email + (selectedUser.username ? ` (${selectedUser.username})` : '')}
                disabled
                helperText="User is fixed when adding an assignment from an existing user's row."
                InputProps={{ readOnly: true }}
              />
            ) : (
              <Autocomplete
                options={availableUsers}
                getOptionLabel={(option) => option.email + (option.username ? ` (${option.username})` : '')}
                loading={loadingUsers}
                value={selectedUser}
                onChange={(_, newValue) => setSelectedUser(newValue)}
                onInputChange={(_, newInputValue) => {
                  loadUsers(newInputValue);
                }}
                filterOptions={(x) => x}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select User"
                    placeholder="Search by email or username..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}

            <Autocomplete
              options={availableCustomers}
              getOptionLabel={(option) => option.red_rock_full_entity_name || option.company_name || 'Unknown'}
              loading={loadingCustomers}
              value={selectedCustomer}
              onChange={(_, newValue) => setSelectedCustomer(newValue)}
              onInputChange={(_, newInputValue) => {
                loadCustomers(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Customer"
                  placeholder="Search customers..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCustomers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <FormControl fullWidth>
              <InputLabel>Access Type</InputLabel>
              <Select
                value={userAccessType}
                onChange={(e) => setUserAccessType(e.target.value as 'can_view' | 'can_edit')}
                label="Access Type"
              >
                <MenuItem value="can_view">Can View</MenuItem>
                <MenuItem value="can_edit">Can Edit</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} disabled={creatingAssignment}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateAssignment}
            variant="contained"
            disabled={creatingAssignment || !selectedUser || !selectedCustomer}
          >
            {creatingAssignment ? <CircularProgress size={20} /> : 'Create Assignment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Assignment Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Assignment</DialogTitle>
        <DialogContent>
          {editingAssignment && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label="Customer"
                value={getCustomerName(editingAssignment)}
                fullWidth
                disabled
                variant="outlined"
              />
              
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editingAssignment.is_active ? 'active' : 'inactive'}
                  onChange={(e) => {
                    const newValue = e.target.value === 'active';
                    setEditingAssignment({
                      ...editingAssignment,
                      is_active: newValue,
                    });
                  }}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={updatingAssignment}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateAssignment}
            variant="contained"
            disabled={updatingAssignment || !editingAssignment}
          >
            {updatingAssignment ? <CircularProgress size={20} /> : 'Update Assignment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserAssignments;

